import { forwardRef, useId, useMemo } from 'react';
import { cn } from '@shared/utils';

interface InputProps {
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  label?: string;
  name?: string;
  type?: 'text' | 'password' | 'date' | 'number';
  inputmode?: 'numeric';
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  value?: string | number;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  isError?: boolean;
  cardFormat?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const id = useId();

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);

    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (props.cardFormat) {
      props.onChange?.(inputValue.replace(/\s/g, '').slice(0, 16));
    } else {
      props.onChange?.(inputValue);
    }
  };

  const displayedValue = useMemo(() => {
    if (props.cardFormat && typeof props.value === 'string') {
      return formatCardNumber(props.value);
    }
    return props.value;
  }, [props.cardFormat, props.value]);

  return (
    <div className={cn('flex flex-col gap-2', props.className)}>
      {props.label && (
        <label htmlFor={id} className={cn('block w-fit font-medium text-black select-none max-md:text-[#4c4c4c80]', props.labelClassName)}>
          {props.label}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        className={cn(
          'h-[40px] rounded-lg border border-[#000000] p-2 font-light text-[#000] outline-none focus:!border-[#e5c496] max-md:h-[50px] max-md:w-full max-md:border-[#4c4c4c1a] max-md:bg-[#fafafa]',
          props.isError && '!border-[#ff0000]',
          props.inputClassName,
        )}
        name={props.name}
        type={props.cardFormat ? 'text' : props.type || 'text'}
        inputMode={props.inputmode}
        placeholder={props.placeholder}
        disabled={props.disabled}
        required={props.required}
        readOnly={props.readOnly}
        minLength={props.minLength}
        maxLength={props.cardFormat ? 19 : props.maxLength}
        pattern={props.pattern}
        value={displayedValue}
        onChange={handleChange}
        onFocus={props.onFocus}
      />
    </div>
  );
});
